import { Dispatch } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { getAwardedPromotions } from '../../../../redux/actions/backoffice/promotions-actions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, DatePicker, Space, Table, Button, Pagination } from 'antd';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    getAwardedPromotionsData,
    getPromotionsErrorData,
    getPromotionsLoadingStateData,
    getTotalAwardedPromotionsAmountData,
    shouldUpdateData,
} from '../../../../redux/selectors/backoffice/propmotions-selectors';
import {
    ICurrencyData,
    IGameItemData,
    IPaginationData,
    IPromotionData,
} from '../../../../helpers/interfaces';
import Search from 'antd/es/input/Search';
import PromotionsColumns from './PromotionsColumns';
import { Link } from 'react-router-dom';
import { localStorageHelper } from '../../../../utils';
import { getGamesByUser } from '../../../../redux/selectors/user-selectors';
import { getAllGamesByUser } from '../../../../redux/actions/user-actions';
import {
    canCreatePromotionCampaign,
    getPromotionCampaignOwnerFilterOptions,
    OPERATOR_CAMPAIGN_OWNER,
    canViewThirdPartyCampaignFeatures,
} from '../../promotionUtils';

const { RangePicker } = DatePicker;

type SortOrder = 'DESCENDING' | 'ASCENDING';

interface SortConfig {
    sortKey: string;
    sortOrder: SortOrder;
}

const DEFAULT_SORT_CONFIG: SortConfig = { sortKey: 'id', sortOrder: 'DESCENDING' };
const DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE = 20;
const SEARCH_OPTIONS = ['name', 'reference', 'gameId'];
const STATUS_OPTIONS = [
    { value: 'UPCOMING', label: 'Upcoming' },
    { value: 'ONGOING', label: 'Ongoing' },
    { value: 'ENDED', label: 'Ended' },
    { value: 'DISABLED', label: 'Disabled' },
];
const DEFAULT_PAGINATION = { pageNumber: 1, pageSize: DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE };

interface IProps {
    getPromotions: (params: any) => void;
    data: IPromotionData[];
    totalAmount: number;
    isLoading: boolean;
    error: string;
    shouldUpdateData: boolean;
}

interface IState {
    filterKey: string;
    filterString: string;
    dateRange: [string, string];
    sortConfig: SortConfig;
    paginationData: IPaginationData;
    isGamesListLoaded: boolean;
    owner?: string;
}

const AwardedPromotions: React.FC<IProps> = ({ getPromotions, data, totalAmount, isLoading }) => {
    const { t } = useTranslation();
    const games = useSelector(getGamesByUser);
    const dispatch = useDispatch();
    const [tableHeight, setTableHeight] = useState(0);
    const [state, setState] = useState({
        filterKey: SEARCH_OPTIONS[0],
        filterString: '',
        dateRange: ['', ''],
        sortConfig: DEFAULT_SORT_CONFIG,
        paginationData: DEFAULT_PAGINATION,
        isGamesListLoaded: false,
        status: '',
        currency: '',
        owner: OPERATOR_CAMPAIGN_OWNER,
    });

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight * 0.8 - 194;
        setTableHeight(height);
    }, []);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    const canUserManageCampaigns = canCreatePromotionCampaign();
    const canViewThirdPartyFeatures = canViewThirdPartyCampaignFeatures();
    const ownerFilterOptions = getPromotionCampaignOwnerFilterOptions();
    const allCurrencies = localStorageHelper.getChainedValue('user.allCurrencies');

    const handlePagination = useCallback(
        (pageNumber: number, pageSize: number = state.paginationData.pageSize) =>
            setState((prevState) => ({
                ...prevState,
                paginationData: { pageNumber, pageSize },
            })),
        [state.paginationData.pageSize],
    );

    const requestPromotions = useCallback(() => {
        setState((prevState) => {
            const {
                paginationData,
                sortConfig,
                filterString,
                dateRange,
                status,
                currency,
                filterKey,
                owner,
            } = prevState;

            getPromotions({
                paginationData,
                sortConfig,
                filterString,
                filterKey: filterKey === 'reference' ? 'externalCampaignId' : filterKey,
                dateRange,
                status,
                currency,
                owner,
            });

            return prevState;
        });
    }, [getPromotions]);
    const columns = PromotionsColumns(canUserManageCampaigns, requestPromotions, dispatch);

    useEffect(() => {
        if (!state.isGamesListLoaded) {
            dispatch(getAllGamesByUser());
        }
    }, [dispatch, state.isGamesListLoaded]);

    useEffect(() => {
        requestPromotions();
    }, [
        requestPromotions,
        state.filterKey,
        state.filterString.length,
        state.dateRange,
        state.status,
        state.currency,
        state.owner,
        state.paginationData,
    ]);

    useEffect(() => {
        if (games?.length > 0) setState((prevState) => ({ ...prevState, isGamesListLoaded: true }));
    }, [games]);

    const getData = useMemo(() => {
        const allGamesWithFreeRounds = games.filter(
            (game: IGameItemData) => game.freeRoundsEnabled,
        );

        const getGamesColumnContent = (games: { id: number; name: string }[]) => {
            if (games.length === allGamesWithFreeRounds.length) return t('all_games');
            if (games.length === 1) return games[0].name;
            return `${games.length} ${games.length % 10 === 1 ? t('game') : t('games')}`;
        };

        return (data || []).map((item) => ({
            ...item,
            key: item.id,
            gamesDisplay: getGamesColumnContent(item.games),
            games: item.games,
        }));
    }, [data, t, games]);

    const onTableChange = useCallback(
        (pagination: any, filters: any, sorter: any) => {
            handlePagination(
                pagination.current ?? 1,
                pagination.pageSize ?? DEFAULT_PAGINATION.pageSize,
            );

            const newSortConfig = Array.isArray(sorter)
                ? sorter[0]?.order
                    ? {
                          sortKey: sorter[0].field.toString(),
                          sortOrder: sorter[0].order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
                      }
                    : DEFAULT_SORT_CONFIG
                : sorter?.field && sorter?.order
                  ? {
                        sortKey: sorter.field.toString(),
                        sortOrder: sorter.order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
                    }
                  : DEFAULT_SORT_CONFIG;

            setState((prevState: any) => ({ ...prevState, sortConfig: newSortConfig }));
        },
        [handlePagination],
    );

    const handleFilterChange = (key: keyof IState, value: any) =>
        setState((prev) => ({ ...prev, [key]: value }));

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (state.filterKey === 'gameId') {
            const numericValue = value.replace(/[^0-9]/g, '');
            handleFilterChange('filterString', numericValue);
        } else {
            handleFilterChange('filterString', value);
        }
    };
    const handleStatusChange = (value: string) => setState((prev) => ({ ...prev, status: value }));
    const handleCurrencyChange = (value: string) =>
        setState((prev) => ({ ...prev, currency: value }));

    const totalWidth: number = columns.reduce(
        (total: number, col: any) => total + (col.width || 0),
        0,
    );

    return (
        <div className="min-h-0 h-[calc(100vh-64px)] overflow-hidden bg-[#fafbfc] flex flex-col pb-4">
            {/* Header Section */}
            <div className="px-6 pt-4 pb-4">
                <div className="flex justify-between items-center">
                    <div>
                        <div className="text-xs text-gray-400 mb-1">{t('all')}</div>
                        <h1 className="text-2xl font-semibold text-gray-900">{t('awarded_feature_promotions')}</h1>
                    </div>
                    {canUserManageCampaigns && (
                        <Link to="/casino/awarded-promotion/new">
                            <Button
                                size="large"
                                shape="round"
                                type="primary"
                                icon={<PlusCircleOutlined />}
                            >
                                Create
                            </Button>
                        </Link>
                    )}
                </div>
            </div>
            <div className="px-6 flex-1 overflow-auto">
                <div className="flex mb-4">
                    <Space.Compact className="flex items-center mr-3">
                        <RangePicker
                            className="w-[300px]"
                            onChange={(date) =>
                                handleFilterChange(
                                    'dateRange',
                                    date ? [date[0], date[1]] : ['', ''],
                                )
                            }
                            placeholder={['Starts from', 'Starts before']}
                        />
                    </Space.Compact>
                    <Space.Compact className="flex items-center mr-3">
                        <Select
                            className="w-[150px]"
                            onChange={handleStatusChange}
                            allowClear
                            placeholder={'Status'}
                        >
                            {STATUS_OPTIONS.map((status) => (
                                <Select.Option key={status.value} value={status.value}>
                                    {status.label}
                                </Select.Option>
                            ))}
                        </Select>
                        <Select
                            className="w-[150px]"
                            onChange={handleCurrencyChange}
                            allowClear
                            placeholder={'Currency'}
                            showSearch
                        >
                            {allCurrencies.map((currency: ICurrencyData) => (
                                <Select.Option key={currency.id} value={currency.code}>
                                    {currency.code}
                                </Select.Option>
                            ))}
                        </Select>
                        {canViewThirdPartyFeatures ? (
                            <Select
                                className="w-[170px]"
                                value={state.owner}
                                onChange={(value) => handleFilterChange('owner', value)}
                                allowClear
                                placeholder={'All owners'}
                            >
                                {ownerFilterOptions.map((owner) => (
                                    <Select.Option key={owner.value} value={owner.value}>
                                        {owner.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        ) : null}
                    </Space.Compact>
                    <Space.Compact className="flex items-center w-full">
                        <Select
                            className="w-[150px]"
                            value={state.filterKey}
                            onChange={(value) => {
                                handleFilterChange('filterKey', value);
                                handleFilterChange('filterString', '');
                            }}
                        >
                            {SEARCH_OPTIONS.map((key) => (
                                <Select.Option key={key} value={key}>
                                    {t(key)}
                                </Select.Option>
                            ))}
                        </Select>
                        <Search
                            className="flex-1"
                            placeholder={t('enter_search_value')}
                            value={state.filterString}
                            onChange={handleSearchChange}
                            suffix={
                                <CloseCircleOutlined
                                    className={`cursor-pointer text-gray-400 hover:text-gray-600 ${state.filterString.length > 0 ? 'opacity-100' : 'opacity-0'}`}
                                    onClick={() => handleFilterChange('filterString', '')}
                                />
                            }
                        />
                    </Space.Compact>
                </div>
                <Table
                    className="w-full pb-4 rounded-lg border border-gray-200 bg-white"
                    scroll={{ x: totalWidth, y: tableHeight }}
                    dataSource={getData}
                    columns={columns}
                    expandable={{
                        showExpandColumn: true,
                        expandedRowRender: (record) => (
                            <>
                                <p>{t('Games in campaign:')}</p>
                                <ul>
                                    {record.games.map((game) => (
                                        <li key={game.id}>
                                            {game.name} | ID: {game.id}
                                        </li>
                                    ))}
                                </ul>
                                <p>
                                    {`${t('Game IDs: ')}${record.games
                                        .map((game) => game.id)
                                        .join(', ')}`}
                                </p>
                            </>
                        ),
                        columnWidth: 40,
                    }}
                    loading={isLoading}
                    size="small"
                    onChange={onTableChange}
                    pagination={false}
                />
                <div className="flex justify-end mt-4">
                    <Pagination
                        current={state.paginationData.pageNumber}
                        pageSize={state.paginationData.pageSize}
                        total={totalAmount}
                        onChange={handlePagination}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                    />
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    data: getAwardedPromotionsData(state),
    totalAmount: getTotalAwardedPromotionsAmountData(state),
    isLoading: getPromotionsLoadingStateData(state),
    error: getPromotionsErrorData(state),
    shouldUpdateData: shouldUpdateData(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getPromotions: (data: any) => dispatch(getAwardedPromotions(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AwardedPromotions);
